import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { TalentEditDialog } from "@/components/admin/TalentEditDialog";
import { Play, Pause, ExternalLink, Pencil } from "lucide-react";

interface TalentTableViewProps {
  talent: Array<{
    id: string;
    name: string;
    slug: string;
    specialty: string;
    specialty_fr?: string | null;
    languages: string[] | null;
    image_url: string | null;
    bio?: string | null;
    bio_fr?: string | null;
    is_active?: boolean;
    is_featured?: boolean;
    display_order?: number;
  }>;
  getImageUrl: (t: { slug: string; image_url: string | null }) => string;
}

type TalentMedia = {
  id: string;
  talent_id: string;
  media_type: string;
  url: string;
  title: string | null;
};

type TalentForEdit = {
  id: string;
  name: string;
  slug: string;
  specialty: string;
  bio: string | null;
  image_url: string | null;
  languages: string[];
  is_featured: boolean;
  is_active: boolean;
  display_order: number;
};

export const TalentTableView = ({ talent, getImageUrl }: TalentTableViewProps) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";
  const { localizedPath } = useLocalizedPath();
  const { hasAdminAccess } = useAuth();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [editingTalent, setEditingTalent] = useState<TalentForEdit | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Helper to get localized specialty
  const getLocalizedSpecialty = (person: { specialty: string; specialty_fr?: string | null }) => {
    if (currentLang === "fr" && person.specialty_fr) {
      return person.specialty_fr;
    }
    return person.specialty;
  };

  // Fetch first audio demo for each talent
  const { data: demosMap } = useQuery({
    queryKey: ["talent-first-demos", talent.map(t => t.id)],
    queryFn: async () => {
      const talentIds = talent.map(t => t.id);
      const { data, error } = await supabase
        .from("talent_media")
        .select("id, talent_id, media_type, url, title")
        .in("talent_id", talentIds)
        .eq("media_type", "audio")
        .order("display_order");
      
      if (error) throw error;

      // Create a map of talent_id -> first audio demo
      const map: Record<string, TalentMedia> = {};
      data?.forEach((demo) => {
        if (!map[demo.talent_id]) {
          map[demo.talent_id] = demo;
        }
      });
      return map;
    },
    enabled: talent.length > 0,
  });

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlay = (talentId: string, demoUrl: string) => {
    if (playingId === talentId) {
      // Stop playing
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      // Start playing new audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(demoUrl);
      audioRef.current.play();
      audioRef.current.onended = () => setPlayingId(null);
      setPlayingId(talentId);
    }
  };

  return (
    <div className="w-full">
      {/* Table Header */}
      <div className={`hidden md:grid gap-4 px-4 py-3 bg-muted/50 rounded-t-lg text-xs uppercase tracking-wide text-muted-foreground font-medium ${hasAdminAccess ? "md:grid-cols-[auto_1fr_1fr_auto_auto_auto]" : "md:grid-cols-[auto_1fr_1fr_auto_auto]"}`}>
        <div className="w-12"></div>
        <div>{t("talentTable.name", "Name")}</div>
        <div>{t("talentTable.specialty", "Specialty")}</div>
        <div className="text-center w-24">{t("talentTable.demo", "Demo")}</div>
        <div className="w-20"></div>
        {hasAdminAccess && <div className="w-10"></div>}
      </div>

      {/* Table Rows */}
      <div className="divide-y divide-border">
        {talent.map((person) => {
          const demo = demosMap?.[person.id];
          const isPlaying = playingId === person.id;

          return (
            <div
              key={person.id}
              className={`grid grid-cols-[auto_1fr] gap-4 px-4 py-4 items-center hover:bg-muted/30 transition-colors ${hasAdminAccess ? "md:grid-cols-[auto_1fr_1fr_auto_auto_auto]" : "md:grid-cols-[auto_1fr_1fr_auto_auto]"}`}
            >
              {/* Avatar */}
              <Avatar className="h-12 w-12">
                <AvatarImage src={getImageUrl(person)} alt={person.name} className="object-cover" />
                <AvatarFallback>{person.name.split(" ").map(n => n[0]).join("")}</AvatarFallback>
              </Avatar>

              {/* Name & Mobile Info */}
              <div className="md:contents">
                <div>
                  <Link 
                    to={localizedPath(`/talent/${person.slug}`)}
                    className="font-medium hover:text-primary transition-colors"
                  >
                    {person.name}
                  </Link>
                  {/* Mobile: Show specialty below name */}
                  <p className="text-sm text-muted-foreground md:hidden">{getLocalizedSpecialty(person)}</p>
                </div>

                {/* Desktop: Specialty column */}
                <div className="hidden md:block text-muted-foreground">
                  {getLocalizedSpecialty(person)}
                </div>

                {/* Demo Play Button */}
                <div className="hidden md:flex justify-center w-24">
                  {demo ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-10 w-10 rounded-full ${isPlaying ? "bg-primary text-primary-foreground" : "hover:bg-primary/10"}`}
                      onClick={() => handlePlay(person.id, demo.url)}
                    >
                      {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>

                {/* View Profile Link */}
                <div className="hidden md:block w-20">
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={localizedPath(`/talent/${person.slug}`)}>
                      <ExternalLink size={14} className="mr-1" />
                      {t("talentTable.view", "View")}
                    </Link>
                  </Button>
                </div>

                {/* Admin Edit Button */}
                {hasAdminAccess && (
                  <div className="hidden md:block w-10">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditingTalent({
                        id: person.id,
                        name: person.name,
                        slug: person.slug,
                        specialty: person.specialty,
                        bio: person.bio || null,
                        image_url: person.image_url,
                        languages: person.languages || [],
                        is_featured: person.is_featured || false,
                        is_active: person.is_active ?? true,
                        display_order: person.display_order || 0,
                      })}
                      aria-label={`Edit ${person.name}`}
                    >
                      <Pencil size={14} />
                    </Button>
                  </div>
                )}
              </div>

              {/* Mobile: Demo & View buttons */}
              <div className="col-span-2 flex gap-2 md:hidden mt-2">
                {demo && (
                  <Button
                    variant="outline"
                    size="sm"
                    className={isPlaying ? "bg-primary text-primary-foreground" : ""}
                    onClick={() => handlePlay(person.id, demo.url)}
                  >
                    {isPlaying ? <Pause size={14} className="mr-1" /> : <Play size={14} className="mr-1" />}
                    {isPlaying ? "Pause" : "Play Demo"}
                  </Button>
                )}
                <Button variant="ghost" size="sm" asChild>
                  <Link to={localizedPath(`/talent/${person.slug}`)}>
                    View Profile
                  </Link>
                </Button>
                {hasAdminAccess && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingTalent({
                      id: person.id,
                      name: person.name,
                      slug: person.slug,
                      specialty: person.specialty,
                      bio: person.bio || null,
                      image_url: person.image_url,
                      languages: person.languages || [],
                      is_featured: person.is_featured || false,
                      is_active: person.is_active ?? true,
                      display_order: person.display_order || 0,
                    })}
                  >
                    <Pencil size={14} className="mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Dialog */}
      {hasAdminAccess && (
        <TalentEditDialog
          talent={editingTalent}
          open={!!editingTalent}
          onOpenChange={(open) => !open && setEditingTalent(null)}
        />
      )}
    </div>
  );
};
